import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { dragonBallAPI, shopAPI } from '@/utils/api';
import { toast } from 'sonner';
import { ShenronWishModal } from './ShenronWishModal';
import { emitEvent, DRAGON_BALL_COLLECTED, RADAR_PEEK } from '@/utils/events';
import { useLocation, useNavigate } from 'react-router-dom';

// Dragon Radar image
const DRAGON_RADAR_IMAGE = 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/ysl2vtzr_kindpng_2369904.png';

// Dragon Ball image URLs for each star count
const DRAGON_BALL_IMAGES = {
  1: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/p36ow0li_1773126108746.webp',
  2: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/6o52t839_1773125788421.webp',
  3: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/apzoyweo_1773125979134.webp',
  4: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/kv5nzwc2_1773125873429.webp',
  5: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/8dnakzdk_1773125927508.png',
  6: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/tnhddhrj_1773126030315.png',
  7: 'https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/hhe81ar1_1773126062388.png',
};

// Export for use in other components
export { DRAGON_BALL_IMAGES };

// Default pages (will be replaced by server-provided list)
const DEFAULT_SPAWN_PAGES = [
  { path: '/library', name: 'Library' },
  { path: '/profile', name: 'Profile' },
  { path: '/friends', name: 'Friends' },
  { path: '/channels', name: 'Channels' },
  { path: '/groups', name: 'Groups' },
  { path: '/leaderboard', name: 'Leaderboard' },
  { path: '/news', name: 'News' },
  { path: '/shop', name: 'Shop' },
  { path: '/inventory', name: 'Inventory' },
];

// Get star name for display
const getStarName = (num) => {
  const names = {
    1: 'One-Star',
    2: 'Two-Star', 
    3: 'Three-Star',
    4: 'Four-Star',
    5: 'Five-Star',
    6: 'Six-Star',
    7: 'Seven-Star'
  };
  return names[num] || `${num}-Star`;
};

// Format cooldown time
const formatCooldown = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

// Collection indicator component for navbar
export const DragonBallIndicator = ({ collected = [], onClick, cooldownRemaining = 0, canCollect = true }) => {
  const total = collected.length;
  
  // Show cooldown state
  if (!canCollect && cooldownRemaining > 0) {
    return (
      <button 
        onClick={onClick}
        className="flex items-center gap-1 px-2 py-1 bg-gray-500/20 rounded-full border border-gray-500/30 hover:border-gray-400/50 transition-colors"
        title={`Dragon Balls scattered! ${formatCooldown(cooldownRemaining)} until they return`}
      >
        <img 
          src={DRAGON_BALL_IMAGES[4]} 
          alt="Dragon Ball"
          className="w-5 h-5 object-contain opacity-50 grayscale"
        />
        <span className="text-xs font-bold text-gray-400">{formatCooldown(cooldownRemaining)}</span>
      </button>
    );
  }
  
  if (total === 7) {
    return (
      <button 
        onClick={onClick}
        data-testid="summon-shenron-btn"
        className="flex items-center gap-1 px-1.5 py-1 bg-gradient-to-r from-green-500/20 to-yellow-500/20 rounded-full border border-green-500/50 hover:border-green-400 transition-colors animate-pulse"
        title="All Dragon Balls collected! Summon Shenron!"
      >
        <span className="text-xs">🐉</span>
        <span className="text-[10px] font-bold text-green-400">SUMMON</span>
      </button>
    );
  }
  
  // Show the highest collected ball or default to 4-star
  const displayBall = collected.length > 0 ? Math.max(...collected) : 4;
  
  return (
    <button 
      onClick={onClick}
      className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full border border-white/10 hover:border-orange-500/30 transition-colors"
      title={`${total}/7 Dragon Balls collected - Click to view collection`}
    >
      <img 
        src={DRAGON_BALL_IMAGES[displayBall]} 
        alt={`${displayBall}-Star Dragon Ball`}
        className="w-5 h-5 object-contain"
      />
      <span className="text-xs font-bold text-gray-400">{total}/7</span>
    </button>
  );
};

// Spawn alert component
const SpawnAlert = ({ spawnState, timeRemaining, hasRadar, onNavigate, onDismiss, accessiblePages }) => {
  if (!spawnState) return null;
  
  const pageName = accessiblePages.find(p => p.path === spawnState.page)?.name || spawnState.page_name;
  
  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      className="fixed top-16 sm:top-20 left-2 right-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[9998] pointer-events-auto max-w-[calc(100vw-1rem)] sm:max-w-none"
    >
      <div className={`backdrop-blur-md border rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-2xl relative ${
        hasRadar 
          ? 'bg-gradient-to-r from-green-600/95 to-cyan-600/95 border-green-400/50 shadow-green-500/30' 
          : 'bg-gradient-to-r from-orange-600/95 to-yellow-600/95 border-orange-400/50 shadow-orange-500/30'
      }`}>
        {/* Dismiss button - positioned inside for better click targeting */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDismiss();
          }}
          className="absolute top-1 right-1 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-colors z-20 cursor-pointer"
          title="Dismiss"
          data-testid="dismiss-dragonball-alert"
        >
          <span className="text-lg leading-none">×</span>
        </button>
        
        {/* Mobile layout: stacked */}
        <div className="flex sm:hidden flex-col gap-2">
          <div className="flex items-center gap-2">
            {hasRadar ? (
              <motion.img 
                src={DRAGON_RADAR_IMAGE} 
                alt="Dragon Radar"
                className="w-8 h-8 object-contain flex-shrink-0"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            ) : (
              <motion.img 
                src={DRAGON_BALL_IMAGES[spawnState.ball_number]} 
                alt="Dragon Ball"
                className="w-8 h-8 object-contain flex-shrink-0"
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-white font-bold text-xs block truncate">
                {getStarName(spawnState.ball_number)} Ball Detected!
              </span>
              {hasRadar ? (
                <span className="text-green-100 text-xs flex items-center gap-1">
                  <span className="text-green-300">📡</span> 
                  <span className="font-bold text-white truncate">{pageName}</span>
                </span>
              ) : (
                <span className="text-orange-100 text-[10px]">
                  Hidden somewhere... Search!
                </span>
              )}
            </div>
            <div className="flex flex-col items-center px-2 py-1 bg-black/30 rounded-lg flex-shrink-0">
              <span className={`text-[8px] uppercase ${hasRadar ? 'text-green-200' : 'text-orange-200'}`}>Time</span>
              <span className={`font-mono font-bold text-sm ${timeRemaining <= 30 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                {formatCooldown(timeRemaining)}
              </span>
            </div>
          </div>
          {hasRadar && (
            <button
              onClick={onNavigate}
              className="w-full px-3 py-2 bg-white/20 hover:bg-white/30 text-white font-bold rounded-lg transition-colors text-sm"
            >
              Go to {pageName}!
            </button>
          )}
        </div>

        {/* Desktop layout: horizontal */}
        <div className="hidden sm:flex items-center gap-3">
          {hasRadar ? (
            <motion.img 
              src={DRAGON_RADAR_IMAGE} 
              alt="Dragon Radar"
              className="w-10 h-10 object-contain"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          ) : (
            <motion.img 
              src={DRAGON_BALL_IMAGES[spawnState.ball_number]} 
              alt="Dragon Ball"
              className="w-10 h-10 object-contain"
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
          <div className="flex flex-col">
            <span className="text-white font-bold text-sm">
              {getStarName(spawnState.ball_number)} Dragon Ball Detected!
            </span>
            {hasRadar ? (
              <span className="text-green-100 text-xs flex items-center gap-1">
                <span className="text-green-300">📡</span> Radar lock: <span className="font-bold text-white">{pageName}</span>
              </span>
            ) : (
              <span className="text-orange-100 text-xs">
                It's hidden somewhere on the site... Search!
              </span>
            )}
          </div>
          <div className="flex flex-col items-center ml-2 px-3 py-1 bg-black/30 rounded-lg">
            <span className={`text-[10px] uppercase ${hasRadar ? 'text-green-200' : 'text-orange-200'}`}>Time Left</span>
            <span className={`font-mono font-bold text-lg ${timeRemaining <= 30 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
              {formatCooldown(timeRemaining)}
            </span>
          </div>
          {hasRadar && (
            <button
              onClick={onNavigate}
              className="ml-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white font-bold rounded-lg transition-colors text-sm"
            >
              Go!
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main Dragon Ball spawner component
export const DragonBallCollector = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collected, setCollected] = useState([]);
  const [canCollect, setCanCollect] = useState(true);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [spawnState, setSpawnState] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isCollecting, setIsCollecting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showOnCurrentPage, setShowOnCurrentPage] = useState(false);
  const [hasRadar, setHasRadar] = useState(false);
  const [hasTimeRing, setHasTimeRing] = useState(false);
  const [accessiblePages, setAccessiblePages] = useState(DEFAULT_SPAWN_PAGES);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [dragonBallsEnabled, setDragonBallsEnabled] = useState(true);
  const audioRef = useRef(null);
  const spawnCheckIntervalRef = useRef(null);
  const cooldownEndRef = useRef(null);
  const pathRef = useRef(location.pathname);
  
  // Load dragon ball setting from user preferences
  useEffect(() => {
    if (user) {
      const enabled = user.settings?.dragon_balls_enabled !== false; // Default true
      setDragonBallsEnabled(enabled);
    }
  }, [user?.settings?.dragon_balls_enabled]);
  
  // Reset dismissed state when spawn changes
  useEffect(() => {
    if (spawnState) {
      setAlertDismissed(false);
    }
  }, [spawnState?.ball_number]);
  
  // Fetch accessible pages based on user role
  useEffect(() => {
    if (user) {
      const fetchAccessiblePages = async () => {
        try {
          const response = await dragonBallAPI.getAccessiblePages();
          if (response.data.pages && response.data.pages.length > 0) {
            setAccessiblePages(response.data.pages);
          }
        } catch (error) {
          console.error('Failed to fetch accessible pages:', error);
          // Fall back to default pages
        }
      };
      fetchAccessiblePages();
    }
  }, [user]);
  
  // Check Dragon Radar status
  useEffect(() => {
    if (user) {
      const checkRadar = async () => {
        try {
          const response = await shopAPI.getDragonRadarStatus();
          setHasRadar(response.data.has_radar);
        } catch (error) {
          console.error('Failed to check Dragon Radar status');
        }
      };
      checkRadar();
      const interval = setInterval(checkRadar, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Check Time Ring status
  useEffect(() => {
    if (user) {
      const checkTimeRing = async () => {
        try {
          const response = await shopAPI.getTimeRingStatus();
          setHasTimeRing(response.data.active);
        } catch (error) {
          console.error('Failed to check Time Ring status');
        }
      };
      checkTimeRing();
      const interval = setInterval(checkTimeRing, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Load collection status from server
  useEffect(() => {
    if (user) {
      loadCollection();
      const interval = setInterval(loadCollection, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Server-synced spawn state - poll server instead of localStorage
  // Pop the corner radar once per spawn per device, even if another device triggered it
  const notifySpawn = (spawn) => {
    const key = `${spawn.ball_number}-${spawn.expires_at}`;
    if (localStorage.getItem('db_spawn_notified') === key) return;
    localStorage.setItem('db_spawn_notified', key);
    emitEvent(RADAR_PEEK, { ball: spawn.ball_number });
  };

  useEffect(() => {
    if (!user || collected.length === 7 || !canCollect || !dragonBallsEnabled) return;
    
    const checkSpawnState = async () => {
      try {
        // First check current spawn state
        const stateResponse = await dragonBallAPI.getSpawnState(pathRef.current);
        const { spawn, can_collect } = stateResponse.data;
        
        if (!can_collect) {
          setCanCollect(false);
          setSpawnState(null);
          return;
        }
        
        if (spawn) {
          setSpawnState(spawn);
          setTimeRemaining(spawn.time_remaining);
          notifySpawn(spawn);
        } else {
          // No active spawn, try to trigger one
          const triggerResponse = await dragonBallAPI.triggerSpawn(pathRef.current);
          if (triggerResponse.data.spawn) {
            setSpawnState(triggerResponse.data.spawn);
            setTimeRemaining(triggerResponse.data.spawn.time_remaining);
            notifySpawn(triggerResponse.data.spawn);
          } else {
            setSpawnState(null);
          }
        }
      } catch (error) {
        console.error('Failed to check spawn state:', error);
      }
    };
    
    // Initial check
    checkSpawnState();

    // Mobile browsers pause timers in the background, so re-check as soon as the tab is visible again
    const onVisible = () => { if (document.visibilityState === 'visible') checkSpawnState(); };
    document.addEventListener('visibilitychange', onVisible);
    
    // Poll server based on Time Ring status
    // Time Ring: spawns every 20 seconds, so poll at 20s
    // Without: poll at 30s
    const pollInterval = hasTimeRing ? 20000 : 30000;
    spawnCheckIntervalRef.current = setInterval(checkSpawnState, pollInterval);
    
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (spawnCheckIntervalRef.current) {
        clearInterval(spawnCheckIntervalRef.current);
      }
    };
  }, [user, collected, canCollect, hasTimeRing, dragonBallsEnabled]);

  // Location is only revealed by the server when we're on the spawn page, so re-check on navigation
  const hasSpawn = !!spawnState;
  useEffect(() => {
    pathRef.current = location.pathname;
    if (!hasSpawn) return;
    dragonBallAPI.getSpawnState(location.pathname).then((r) => {
      if (r.data.spawn) setSpawnState(r.data.spawn);
    }).catch(() => {});
  }, [location.pathname, hasSpawn]);
  
  // Position is picked once per spawn per page visit, so polling doesn't make the ball jump around
  const spawnKey = spawnState?.page && (location.pathname === spawnState.page || location.pathname.startsWith(spawnState.page + '/'))
    ? `${spawnState.ball_number}-${spawnState.expires_at}` : null;
  const spawnPosRef = useRef({ key: null, pos: null });
  useEffect(() => {
    if (spawnKey && spawnPosRef.current.key === spawnKey) {
      setPosition(spawnPosRef.current.pos);
      setShowOnCurrentPage(true);
    } else if (spawnKey) {
      // Stay inside the scrollable document; full-height pages like Channels can't scroll
      const pageWidth = Math.max(document.documentElement.scrollWidth, window.innerWidth);
      const pageHeight = Math.max(document.documentElement.scrollHeight, window.innerHeight);
      const ballSize = 64;
      const navbarHeight = 80;
      const padding = 60;
      
      const x = padding + Math.random() * Math.max(0, pageWidth - ballSize - padding * 2);
      const y = navbarHeight + padding + Math.random() * Math.max(0, pageHeight - ballSize - navbarHeight - padding * 2);
      
      spawnPosRef.current = { key: spawnKey, pos: { x, y } };
      setPosition({ x, y });
      setShowOnCurrentPage(true);
    } else {
      setShowOnCurrentPage(false);
    }
  }, [spawnKey]);
  
  // Timer countdown (client-side countdown, server is source of truth)
  useEffect(() => {
    if (!spawnState || timeRemaining <= 0) return;
    
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - clear spawn state
          setSpawnState(null);
          setShowOnCurrentPage(false);
          toast.error('The Dragon Ball vanished! It escaped...', { duration: 3000 });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [spawnState]);
  
  // Server-synced cooldown timer
  useEffect(() => {
    if (!cooldownEndRef.current || canCollect) return;
    
    const updateCooldown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((cooldownEndRef.current - now) / 1000));
      
      if (remaining <= 0) {
        setCanCollect(true);
        setCooldownRemaining(0);
        cooldownEndRef.current = null;
        loadCollection();
      } else {
        setCooldownRemaining(remaining);
      }
    };
    
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [canCollect]);
  
  const loadCollection = async () => {
    try {
      const response = await dragonBallAPI.getCollection();
      setCollected(response.data.collected || []);
      setCanCollect(response.data.can_collect);
      
      if (!response.data.can_collect && response.data.cooldown_remaining > 0) {
        cooldownEndRef.current = Date.now() + (response.data.cooldown_remaining * 1000);
        setCooldownRemaining(response.data.cooldown_remaining);
      } else {
        cooldownEndRef.current = null;
        setCooldownRemaining(0);
      }
    } catch (error) {
      console.error('Failed to load Dragon Ball collection');
    }
  };
  
  // Handle navigation to spawn page (when radar equipped)
  const handleNavigateToSpawn = () => {
    if (spawnState) {
      navigate(spawnState.page);
    }
  };
  
  // Handle collection
  const handleCollect = async () => {
    if (!spawnState || isCollecting) return;
    
    setIsCollecting(true);
    
    try {
      const response = await dragonBallAPI.collect(spawnState.ball_number);
      
      // Play collect sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
      
      // Update local state
      setCollected(response.data.collected);
      
      // Clear spawn state
      setSpawnState(null);
      setShowOnCurrentPage(false);
      setTimeRemaining(0);
      
      // Emit event to notify other components
      emitEvent(DRAGON_BALL_COLLECTED, { 
        collected: response.data.collected,
        complete: response.data.complete 
      });
      
      // Show success toast
      if (response.data.complete) {
        toast.success('🐉 You collected all 7 Dragon Balls! Make a wish!', {
          duration: 5000,
        });
      } else {
        toast.success(`⭐ ${response.data.message}`, {
          duration: 3000,
        });
      }
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to collect Dragon Ball';
      toast.error(errorMsg);
    } finally {
      setIsCollecting(false);
    }
  };
  
  if (!user || collected.length === 7) return null;
  
  return (
    <>
      {/* Hidden audio element for collect sound */}
      <audio 
        ref={audioRef} 
        src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6WjXx0dX+Pn6edmYx8b2x0g5OgnpqNfnBrb3yLmZ+bk4V3bm11hJSdnZaKfHJvcH2KlpqYk4l+dHJ0fYmSlZWRi4N7d3Z4f4eNkJCOioWAe3h4e4GGiouLiYaEgX58e3t9gIOGh4eGhYOBf358fH1/gYOEhISDgoGAf35+fn5/gIGBgYGAgH9/f39/f4CAgICAgH9/f39/f39/gICAgIB/f39/f39/f39/gIB/f39/f39/f39/f39/f39/"
        preload="auto"
      />
      
      {/* Old detection popup removed — the Dragon Radar (components/radar) replaces it */}
      
      {/* Timer when on correct page */}
      <AnimatePresence>
        {showOnCurrentPage && spawnState && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-20 inset-x-0 mx-auto w-max max-w-[calc(100vw-1rem)] z-[9998]"
          >
            <div className="bg-gradient-to-r from-green-600/95 to-emerald-600/95 backdrop-blur-md border border-green-400/50 rounded-xl px-4 py-2 shadow-2xl">
              <div className="flex items-center gap-3">
                <span className="text-white font-bold text-sm">Find the Dragon Ball!</span>
                <div className="flex items-center gap-1 px-2 py-1 bg-black/30 rounded-lg">
                  <span className={`font-mono font-bold ${timeRemaining <= 30 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                    {formatCooldown(timeRemaining)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Spawned Dragon Ball - only shows on the correct page (CSS animations so it can't get stuck invisible) */}
      {showOnCurrentPage && spawnState && (
        <div className="pointer-events-none absolute top-0 left-0 w-full h-full overflow-visible z-[9999]">
          <button
            onClick={handleCollect}
            disabled={isCollecting}
            className="db-spawn absolute cursor-pointer pointer-events-auto"
            style={{
              left: position.x,
              top: position.y,
              filter: 'drop-shadow(0 0 20px rgba(255, 150, 0, 0.8)) drop-shadow(0 0 40px rgba(255, 100, 0, 0.5))',
            }}
            title={`${getStarName(spawnState.ball_number)} Dragon Ball! Click to collect!`}
            data-testid="spawned-dragon-ball"
          >
            <img
              src={DRAGON_BALL_IMAGES[spawnState.ball_number]}
              alt={`${spawnState.ball_number}-Star Dragon Ball`}
              className="w-16 h-16 object-contain"
            />
            <div className="db-sparkles absolute inset-0 pointer-events-none">
              {[0, 60, 120, 180, 240, 300].map((angle) => (
                <span
                  key={angle}
                  className="absolute left-1/2 top-1/2 w-2 h-2 -ml-1 -mt-1"
                  style={{ transform: `rotate(${angle}deg) translateY(-40px)` }}
                >
                  <span className="db-sparkle block w-2 h-2 bg-yellow-300 rounded-full" style={{ animationDelay: `${angle / 360}s` }} />
                </span>
              ))}
            </div>
          </button>
        </div>
      )}
    </>
  );
};

// Collection modal/panel component
export const DragonBallCollection = ({ collected = [], onClose, onSummon, cooldownRemaining = 0, canCollect = true }) => {
  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[#0F0F1A] border border-orange-500/30 rounded-xl p-5 w-full max-w-sm shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
        style={{ margin: 'auto' }}
      >
        <h2 className="text-xl font-heading text-center mb-3 text-orange-400">
          Dragon Ball Collection
        </h2>
        
        {/* Cooldown message */}
        {!canCollect && cooldownRemaining > 0 ? (
          <p className="text-center text-gray-400 text-sm mb-4">
            The Dragon Balls have scattered! They will return in {formatCooldown(cooldownRemaining)}.
          </p>
        ) : (
          <p className="text-center text-gray-400 text-sm mb-4">
            {collected.length === 7 
              ? "You've collected all the Dragon Balls! Summon Shenron!" 
              : `Dragon Balls spawn randomly on different pages. You have 1m 30s to find them!`}
          </p>
        )}
        
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 mb-4">
          {[1, 2, 3, 4, 5, 6, 7].map((num) => {
            const isCollected = collected.includes(num);
            return (
              <div 
                key={num}
                className={`relative flex flex-col items-center ${!isCollected && 'opacity-30 grayscale'}`}
                title={isCollected ? `${getStarName(num)} Ball - Collected!` : `${getStarName(num)} Ball - Not found`}
              >
                <img 
                  src={DRAGON_BALL_IMAGES[num]} 
                  alt={`${num}-Star Dragon Ball`}
                  className={`w-12 h-12 sm:w-10 sm:h-10 object-contain ${isCollected ? 'dragon-ball-glow' : ''}`}
                />
                <span className={`text-[10px] mt-1 ${isCollected ? 'text-orange-400' : 'text-gray-500'}`}>
                  {num}★
                </span>
                {isCollected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <span className="text-white text-[10px]">✓</span>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full border border-orange-500/30">
            <span className="text-xl">🐉</span>
            <span className="text-lg font-bold text-orange-400">{collected.length}/7</span>
          </div>
        </div>
        
        {/* Summon button when all 7 collected */}
        {collected.length === 7 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <button
              onClick={() => {
                onClose();
                setTimeout(() => {
                  onSummon();
                }, 100);
              }}
              className="w-full py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-lg hover:from-green-500 hover:to-green-400 transition-all shadow-[0_0_20px_rgba(34,197,94,0.5)] animate-pulse"
            >
              🐉 SUMMON SHENRON 🐉
            </button>
            <p className="text-center mt-2 text-sm text-green-400 italic">
              "Eternal Dragon, by your name I summon you!"
            </p>
          </motion.div>
        )}
        
        <button
          onClick={onClose}
          className="w-full py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
};

export default DragonBallCollector;
